import { useState, useCallback } from 'react';
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Upload, File, X, ArrowRight, AlertCircle, FileText, ChevronDown, ChevronUp, Info } from "lucide-react";
import { useDropzone } from 'react-dropzone';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { parseResumeFile } from "@/lib/pdf-parser";
import { useToast } from "@/components/ui/use-toast";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface LocationState {
  company: string;
  jobDescription: string;
  experienceLevel?: 'entry' | 'mid' | 'senior';
}

const interviewGuidelines = [
  "Find a quiet room with minimal background noise",
  "Use a good quality microphone or headset if possible",
  "Wait for the AI interviewer to complete their questions before responding",
  "Speak clearly and at a normal pace",
  "Take your time to think before answering",
  "You can ask the interviewer to repeat a question if needed",
  "The interview will be conducted in a conversational manner",
  "Be professional and treat it like a real interview"
];

export default function UploadResume() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { company, jobDescription, experienceLevel } = (location.state as LocationState) || {};
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isJDExpanded, setIsJDExpanded] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(true);

  // Redirect if no company is selected
  if (!company) {
    navigate('/select-company');
    return null;
  }

  const onDrop = useCallback(async (acceptedFiles: File[], fileRejections: any[]) => {
    if (fileRejections.length > 0) {
      setError("Please upload a valid PDF, DOC, DOCX, or TXT file under 5MB");
      return;
    }

    if (acceptedFiles.length > 0) {
      setIsUploading(true);
      setError("");
      
      try {
        const file = acceptedFiles[0];
        const text = await parseResumeFile(file);
        
        if (text) {
          setUploadedFile(file);
          setResumeText(text);
          toast({
            title: "Resume uploaded successfully",
            description: "Your resume has been processed and is ready for the interview.",
          });
        }
      } catch (err) {
        setError("Error processing file. Please try again.");
        setUploadedFile(null);
        setResumeText("");
      } finally {
        setIsUploading(false);
      }
    }
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    maxSize: 5242880
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedFile(null);
    setResumeText("");
    setError("");
  };

  const handleNext = () => {
    if (uploadedFile && resumeText) {
      console.log('Navigating to interview with resume content length:', resumeText.length);
      navigate('/interview', {
        state: {
          company,
          jobDescription,
          resumeText
        }
      });
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-background/50 to-background overflow-hidden">
      <AnimatedBackground />
      
      <div className="container relative z-10 mx-auto max-w-4xl px-4 py-20">
        {showGuidelines && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Alert className="bg-card/50 backdrop-blur-sm border-primary/50">
              <Info className="h-4 w-4 text-primary" />
              <AlertTitle className="text-foreground font-semibold mb-2">Interview Guidelines</AlertTitle>
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground dark:text-foreground/80">
                  {interviewGuidelines.map((guideline, index) => (
                    <li key={index}>{guideline}</li>
                  ))}
                </ul>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 bg-primary/10 hover:bg-primary/20 text-primary font-medium transition-colors" 
                  onClick={() => setShowGuidelines(false)}
                >
                  Dismiss
                </Button>
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-blue-600 dark:from-white dark:to-white/80 bg-clip-text text-transparent">
            Upload Your Resume
          </h1>
          <p className="text-muted-foreground dark:text-foreground/80 text-lg">
            Let's personalize your interview experience for {company.charAt(0).toUpperCase() + company.slice(1)}
          </p>
        </motion.div>

        {jobDescription && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Collapsible
              open={isJDExpanded}
              onOpenChange={setIsJDExpanded}
              className="bg-card/50 backdrop-blur-sm rounded-lg border overflow-hidden"
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full p-4 flex items-center justify-between hover:bg-accent"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-foreground">{experienceLevel && (
                      <span className="text-foreground/90">
                        Job Description ({experienceLevel.charAt(0).toUpperCase() + experienceLevel.slice(1)} Level)
                      </span>
                    )}</span>
                  </div>
                  {isJDExpanded ? (
                    <ChevronUp className="w-5 h-5 text-foreground/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-foreground/60" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 text-sm text-foreground/90 whitespace-pre-wrap">
                  {jobDescription}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-8"
        >
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!uploadedFile ? (
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-xl p-12",
                "bg-card/50 backdrop-blur-sm",
                "transition-all duration-300",
                isDragActive ? "border-primary scale-[1.02]" : "border-border",
                "cursor-pointer hover:border-primary/50 hover:scale-[1.01]",
                "relative group"
              )}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center space-y-4">
                <motion.div
                  animate={isUploading ? { rotate: 360 } : { y: [0, -8, 0] }}
                  transition={isUploading ? 
                    { duration: 1, repeat: Infinity, ease: "linear" } : 
                    { duration: 2, repeat: Infinity }}
                  className="relative"
                >
                  <div className="absolute -inset-1 rounded-full bg-primary/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                  {isUploading ? (
                    <FileText className="w-12 h-12 text-primary relative" />
                  ) : (
                    <Upload className="w-12 h-12 text-muted-foreground relative" />
                  )}
                </motion.div>
                <div className="text-center">
                  <p className="text-lg font-medium">
                    {isUploading ? "Uploading..." : 
                     isDragActive ? "Drop your resume here" : 
                     "Drag & drop your resume"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    or click to browse (PDF, DOC, DOCX)
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Maximum file size: 5MB
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card/50 backdrop-blur-sm rounded-xl p-6 border relative group"
            >
              <div className="absolute -inset-px rounded-xl bg-gradient-to-r from-primary/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <File className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{uploadedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDelete}
                  className="hover:text-destructive"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          )}

          <div className="flex justify-between items-center pt-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/select-company`)}
              className="hover:bg-primary/10"
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!uploadedFile || isUploading}
              className="group relative overflow-hidden"
            >
              Continue to Interview
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              <motion.span
                className="absolute inset-0 bg-white/20"
                animate={{
                  transform: ["translateX(-100%)", "translateX(100%)"],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "loop",
                }}
              />
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 